// Frontend mirror of backend/src/services/campaignContentService.js.
//
// `campaign.generatedContent` v2 holds an append-only list of generation runs
// plus a cross-run selection referenced by globally-unique `uid`s. These pure
// helpers normalize legacy shapes, resolve the selection, and produce the
// immutable updates the Create page needs. The backend re-derives the flat
// `text` on every write, so the UI only needs to keep `selection` correct.

export interface GenTextOption {
  uid: string;
  id: string;
  label?: string;
  content: string;
  metadata?: Record<string, unknown>;
  templateName?: string;
  header?: string;
  footer?: string;
  variableMapping?: Record<string, string>;
}

export interface GenMediaOption {
  uid: string;
  id: string;
  url?: string;
  metadata?: Record<string, unknown> & { error?: string; brief?: string; angle?: string };
}

export interface Generation {
  id: string;
  label: string;
  jobId?: string | null;
  createdAt?: string | null;
  contentTypes: string[];
  inputs?: Record<string, unknown> | null;
  textOptions: GenTextOption[];
  photoOptions: GenMediaOption[];
  videoOptions: GenMediaOption[];
}

export interface Selection {
  textUid: string | null;
  photoUids: string[];
  videoUids: string[];
}

export interface GeneratedContentV2 {
  schemaVersion: 2;
  generations: Generation[];
  selection: Selection;
  text: string;
  selectedOptionIndex?: number;
  selectedWhatsappTemplateId?: string;
  jobId?: string | null;
  lastGeneratedAt?: string | null;
  images?: Array<{ mimeType: string; data: string }>;
}

export type TaggedTextOption = GenTextOption & { genId: string; genLabel: string };
export type TaggedMediaOption = GenMediaOption & { genId: string; genLabel: string };

export function emptyContent(): GeneratedContentV2 {
  return {
    schemaVersion: 2,
    generations: [],
    selection: { textUid: null, photoUids: [], videoUids: [] },
    text: '',
  };
}

function stampOptions<T extends { id?: string; uid?: string }>(
  arr: T[] | undefined,
  genId: string,
  type: string,
): (T & { id: string; uid: string })[] {
  return (Array.isArray(arr) ? arr : []).map((o, i) => {
    const id = o.id || `${type}-${i + 1}`;
    return { ...o, id, uid: o.uid || `${genId}-${id}` };
  });
}

function ensureSelection(sel: Partial<Selection> | undefined): Selection {
  return {
    textUid: sel?.textUid ?? null,
    photoUids: Array.isArray(sel?.photoUids) ? [...sel!.photoUids] : [],
    videoUids: Array.isArray(sel?.videoUids) ? [...sel!.videoUids] : [],
  };
}

function pruneSelection(gc: GeneratedContentV2): GeneratedContentV2 {
  const text = new Set<string>();
  const photo = new Set<string>();
  const video = new Set<string>();
  for (const g of gc.generations) {
    g.textOptions.forEach((o) => text.add(o.uid));
    g.photoOptions.forEach((o) => photo.add(o.uid));
    g.videoOptions.forEach((o) => video.add(o.uid));
  }
  const sel = gc.selection;
  if (sel.textUid && !text.has(sel.textUid)) sel.textUid = null;
  sel.photoUids = sel.photoUids.filter((u) => photo.has(u));
  sel.videoUids = sel.videoUids.filter((u) => video.has(u));
  return gc;
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export function normalize(raw: any): GeneratedContentV2 {
  if (!raw || typeof raw !== 'object') return emptyContent();

  if (Array.isArray(raw.generations)) {
    const gc: GeneratedContentV2 = {
      ...raw,
      schemaVersion: 2,
      generations: raw.generations.map((g: Generation) => ({
        ...g,
        textOptions: stampOptions(g.textOptions, g.id, 'text'),
        photoOptions: stampOptions(g.photoOptions, g.id, 'photo'),
        videoOptions: stampOptions(g.videoOptions, g.id, 'video'),
      })),
      selection: ensureSelection(raw.selection),
    };
    pruneSelection(gc);
    gc.text = buildSelectionText(gc);
    return gc;
  }

  // Legacy flat shape -> single 'gen-1'.
  const genId = 'gen-1';
  const textOptions = stampOptions(raw.textOptions, genId, 'text');
  const photoOptions = stampOptions(raw.photoOptions, genId, 'photo');
  const videoOptions = stampOptions(raw.videoOptions, genId, 'video');
  const selIdx = Number.isInteger(raw.selectedOptionIndex) ? raw.selectedOptionIndex : 0;
  const selectedText = textOptions[selIdx] || textOptions[0] || null;
  const contentTypes = [
    textOptions.length ? 'text' : null,
    photoOptions.length ? 'photo' : null,
    videoOptions.length ? 'video' : null,
  ].filter(Boolean) as string[];
  const hasAny = textOptions.length || photoOptions.length || videoOptions.length;

  return {
    schemaVersion: 2,
    generations: hasAny
      ? [{
          id: genId,
          label: 'Option 1',
          jobId: raw.jobId || null,
          createdAt: raw.lastGeneratedAt || null,
          contentTypes,
          inputs: null,
          textOptions,
          photoOptions,
          videoOptions,
        }]
      : [],
    selection: { textUid: selectedText?.uid || null, photoUids: [], videoUids: [] },
    text: typeof raw.text === 'string' ? raw.text : (selectedText?.content || ''),
    selectedOptionIndex: selIdx,
    selectedWhatsappTemplateId: raw.selectedWhatsappTemplateId,
    jobId: raw.jobId || null,
    lastGeneratedAt: raw.lastGeneratedAt || null,
    images: Array.isArray(raw.images) ? raw.images : [],
  };
}

export function buildSelectionText(gc: GeneratedContentV2): string {
  const uid = gc.selection?.textUid;
  let first: GenTextOption | null = null;
  for (const g of gc.generations) {
    for (const o of g.textOptions) {
      if (first === null) first = o;
      if (uid && o.uid === uid) return o.content || '';
    }
  }
  return first?.content || '';
}

export function allTextOptions(gc: GeneratedContentV2): TaggedTextOption[] {
  return gc.generations.flatMap((g) => g.textOptions.map((o) => ({ ...o, genId: g.id, genLabel: g.label })));
}
export function allPhotoOptions(gc: GeneratedContentV2): TaggedMediaOption[] {
  return gc.generations.flatMap((g) => g.photoOptions.map((o) => ({ ...o, genId: g.id, genLabel: g.label })));
}
export function allVideoOptions(gc: GeneratedContentV2): TaggedMediaOption[] {
  return gc.generations.flatMap((g) => g.videoOptions.map((o) => ({ ...o, genId: g.id, genLabel: g.label })));
}

export function selectedText(gc: GeneratedContentV2): TaggedTextOption | null {
  return allTextOptions(gc).find((o) => o.uid === gc.selection.textUid) ?? null;
}
export function selectedPhotos(gc: GeneratedContentV2): TaggedMediaOption[] {
  return allPhotoOptions(gc).filter((o) => gc.selection.photoUids.includes(o.uid));
}
export function selectedVideos(gc: GeneratedContentV2): TaggedMediaOption[] {
  return allVideoOptions(gc).filter((o) => gc.selection.videoUids.includes(o.uid));
}

// ---- immutable updates (each returns a fresh normalized object) ----

function clone(gc: GeneratedContentV2): GeneratedContentV2 {
  return {
    ...gc,
    generations: gc.generations.map((g) => ({ ...g })),
    selection: ensureSelection(gc.selection),
  };
}

export function setTextSelection(gc: GeneratedContentV2, uid: string | null): GeneratedContentV2 {
  const next = clone(gc);
  next.selection.textUid = uid;
  next.text = buildSelectionText(next);
  return next;
}

export function togglePhoto(gc: GeneratedContentV2, uid: string): GeneratedContentV2 {
  const next = clone(gc);
  next.selection.photoUids = next.selection.photoUids.includes(uid)
    ? next.selection.photoUids.filter((u) => u !== uid)
    : [...next.selection.photoUids, uid];
  return next;
}

export function toggleVideo(gc: GeneratedContentV2, uid: string): GeneratedContentV2 {
  const next = clone(gc);
  next.selection.videoUids = next.selection.videoUids.includes(uid)
    ? next.selection.videoUids.filter((u) => u !== uid)
    : [...next.selection.videoUids, uid];
  return next;
}

// Swap in freshly signed photo/video URLs from `fresh` (the same media, just re-read), matched by
// uid. Only the URLs change — selection and text stay exactly as they are on screen. Returns `gc`
// itself when nothing changed, so a refresh that brings nothing new does not re-render.
export function withFreshUrls(gc: GeneratedContentV2, fresh: GeneratedContentV2): GeneratedContentV2 {
  const urls = new Map<string, string>();
  for (const o of [...allPhotoOptions(fresh), ...allVideoOptions(fresh)]) {
    if (o.url) urls.set(o.uid, o.url);
  }
  let changed = false;
  const swap = (o: GenMediaOption): GenMediaOption => {
    const url = urls.get(o.uid);
    if (!url || url === o.url) return o;
    changed = true;
    return { ...o, url };
  };
  const generations = gc.generations.map((g) => ({
    ...g,
    photoOptions: g.photoOptions.map(swap),
    videoOptions: g.videoOptions.map(swap),
  }));
  return changed ? { ...gc, generations } : gc;
}

// Remove an entire generation ("Discard option"), clean up any selection uids
// that pointed at it, and recompute the flat text.
export function removeGeneration(gc: GeneratedContentV2, genId: string): GeneratedContentV2 {
  const next = clone(gc);
  next.generations = next.generations.filter((g) => g.id !== genId);
  pruneSelection(next);
  next.text = buildSelectionText(next);
  return next;
}

export function generationCount(gc: GeneratedContentV2 | null | undefined): number {
  return gc?.generations?.length ?? 0;
}
